import { useContext, useEffect, useState } from 'react';
import { Fade, Stack, Chip, Typography, Button, InputAdornment, Box, OutlinedInput } from '@mui/material';
import { SearchIcon } from 'lucide-react';
import { MetrcLogo, metrcTagsClasses, roomDetailClasses, tableClasses, DetailAndDelBtn } from '../../utility';
import apiService from '../../../api/apiService';
import { FeedbackContext } from '../../../App';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';
import DriverEntryPopup from '../Popup/DriverEntryPopup';
import DeleteAlertBox from '../../utility/messages/DeleteAlertBox';

const driverKeyTitleMap = [
  { key: 'driver_name', title: 'Driver Name' },
  { key: 'driver_license_number', title: 'License Number' },
  { key: 'employee_id', title: 'Employee ID' },
];

export default function _TransportersTab({ currDarkThemeClass, farmContextValue, triggerAddNewDriver, handleSetValues }) {
  const feedbackCX = useContext(FeedbackContext);

  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [detailChildren, setDetailChildren] = useState(null);

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);

  useEffect(() => {
    if (farmContextValue && Array.isArray(farmContextValue.farms) && farmContextValue.farms.length > 0) {
      setFarms(farmContextValue.farms);
      const defaultFarm = farmContextValue.farms[0];
      setSelectedFarm(defaultFarm);
      setSelectedFilter(defaultFarm.farm_id);
    }
  }, [farmContextValue]);

  useEffect(() => {
    if (triggerAddNewDriver) {
      handleAddNewDriver();
      handleSetValues('triggerAddNewDriver', false);
    }
  }, [triggerAddNewDriver, handleSetValues]);

  const handlePopupClose = () => {
    setDetailChildren(null);
    setOpen(false);
  };

  const handleSetSelDriver = (prop, value) => {
    setDetailChildren(state => ({
      ...state,
      selDriver: {
        ...state?.selDriver,
        [prop]: value,
      },
    }));
  };

  const handleDeleteClick = driver => {
    setDriverToDelete(driver);
    setDeleteAlertOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!driverToDelete) return;

    try {
      const url = `transporters/drivers/${driverToDelete.id}`;
      await apiService.deleteInstance(url);
      await fetchDrivers();
      feedbackCX.setContextValue(true, {
        type: 'success',
        message: 'Driver deleted successfully.',
      });
    } catch (err) {
      console.error('Error deleting driver:', err);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: err?.response?.data?.error || 'Error deleting driver.',
      });
    } finally {
      setDriverToDelete(null);
    }
  };

  const getDriverActionBtnProps = driver => ({
    currDarkThemeClass,
    toShowDelBtn: true,
    sx: { justifyContent: 'flex-start' },
    onDetailClick: () => handleDriverEntryView(driver),
    onDelClick: () => handleDeleteClick(driver),
  });

  const formatData = items =>
    items.map((item, idx) => [
      `${idx + 1 + page * rowsPerPage}`.padStart(2, '0'),
      ...driverKeyTitleMap.map(({ key }) => {
        const val = item[key];
        return val ?? 'N/A';
      }),
      <DetailAndDelBtn key={`action-${item.id}`} {...getDriverActionBtnProps(item)} />,
    ]);

  const fetchDrivers = async () => {
    if (!selectedFarm) return;

    setLoading(true);
    try {
      let url = `transporters/drivers?license_number=${encodeURIComponent(selectedFarm.license_number)}&limit=${rowsPerPage}&page=${page + 1}`;

      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await apiService.fetchInstance(url);
      if (response?.data) {
        const results = response.data.results || response.data || [];
        setData(formatData(results));
        setTotalCount(response.data.count ?? results.length ?? 0);
      }
    } catch (error) {
      console.error('Error fetching drivers', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Error fetching drivers.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [selectedFarm, page, rowsPerPage, searchTerm]);

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleFilterChange = farmId => {
    const farmObj = farms.find(f => f.farm_id === farmId) || null;
    setSelectedFarm(farmObj);
    setSelectedFilter(farmId);
    setPage(0);
  };

  const sChipsProps = farm => ({
    key: farm.farm_id,
    label: farm.farm_name,
    variant: selectedFilter === farm.farm_id ? 'filled' : 'outlined',
    onClick: () => handleFilterChange(farm.farm_id),
    clickable: true,
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
  });

  const metrcTagFarmChipsList = (
    <Stack direction="row" spacing={1}>
      {farms.length > 0 ? farms.map(farm => <Chip key={farm.farm_id} {...sChipsProps(farm)} />) : <Typography>No farms available</Typography>}
    </Stack>
  );

  const metrcTagFilterWrapperProps = {
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const metrcTagFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
  };

  const metrcTagFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const handleSearch = event => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') event.preventDefault();
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search by Driver Name',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon size={16} />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  const handleSyncToMetrc = async () => {
    if (!selectedFarm) return;
    setSyncLoading(true);

    const url = `transporters/drivers?license_number=${encodeURIComponent(selectedFarm.license_number)}&sync_to_metrc=true`;

    try {
      const response = await apiService.fetchInstance(url);
      if (response?.status === 200) {
        await fetchDrivers();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Drivers synced from Metrc successfully.',
        });
      }
    } catch (error) {
      console.error('Error syncing drivers from Metrc', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: error?.response?.data?.error || 'Error syncing drivers from Metrc.',
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleEntitySave = async newDriver => {
    try {
      const driverData = {
        ...newDriver,
        farm: selectedFarm.farm_id,
      };

      await apiService.createInstance('transporters/drivers', driverData);
      await fetchDrivers();
      feedbackCX.setContextValue(true, {
        type: 'success',
        message: 'Driver created successfully.',
      });
      return { success: true };
    } catch (err) {
      console.error('Error creating driver:', err);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: err?.response?.data?.error || 'Error creating driver.',
      });
      return { success: false };
    }
  };

  const handleEntityEntryModify = async updatedDriver => {
    try {
      const driverData = {
        ...updatedDriver,
        farm: updatedDriver.farm || selectedFarm.farm_id,
      };

      await apiService.modifyInstance(`transporters/drivers/${driverData.id}`, driverData);
      await fetchDrivers();
      feedbackCX.setContextValue(true, {
        type: 'success',
        message: 'Driver updated successfully.',
      });
      return { success: true };
    } catch (err) {
      console.error('Error updating driver:', err);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: err?.response?.data?.error || 'Error updating driver.',
      });
      return { success: false };
    }
  };

  const handleDriverEntryView = driver => {
    setOpen(true);
    setDetailChildren({
      selDriver: { ...driver },
      title: driver.driver_name,
      farmName: selectedFarm?.farm_name,
      toShowSave: true,
      handleDriverSave: handleEntityEntryModify,
      handleSetSelDriver,
    });
  };

  const handleAddNewDriver = () => {
    if (!selectedFarm) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Please select a farm first.',
      });
      return;
    }
    setOpen(true);
    setDetailChildren({
      selDriver: {
        farm: selectedFarm.farm_id,
      },
      title: 'New Driver',
      farmName: selectedFarm?.farm_name,
      toShowSave: true,
      handleDriverSave: handleEntitySave,
      handleSetSelDriver,
    });
  };

  const tableTitles = [
    { title: 'Sl. No', width: 80 },
    ...driverKeyTitleMap.map(e => ({ title: e.title })),
    { title: 'Actions', width: 120, align: 'center' },
  ];

  const displayProps = {
    animationClass: 'driversTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Drivers Found',
    entity: 'Drivers',
    tableEntries: data,
    tableTitles,
    toShowAddBtn: false,
    onAddClick: handleAddNewDriver,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading,
  };

  const driverEntryPopupProps = {
    currDarkThemeClass,
    open,
    onClose: handlePopupClose,
    ...detailChildren,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <Stack {...metrcTagFilterProps} sx={{ flex: 1, minWidth: 0 }}>
            <Typography {...metrcTagFilterTypoProps} sx={{ whiteSpace: 'nowrap' }}>
              Show by Farm:
            </Typography>
            <Box
              sx={{
                flex: 1,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: 1,
                  minWidth: 'max-content',
                  pb: 0.5,
                }}
              >
                {metrcTagFarmChipsList}
              </Box>
            </Box>
          </Stack>

          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={handleSyncToMetrc}
              disabled={syncLoading || !selectedFarm}
              style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
              sx={{
                color: '#B1D23A',
                '&.Mui-disabled': { color: '#B1D23A' },
              }}
            >
              <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
              {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
            </Button>

            <OutlinedInput {...outlinedInputProps} />
          </Stack>
        </Stack>

        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...displayProps} />
        </Box>

        {open && <DriverEntryPopup {...driverEntryPopupProps} />}

        <DeleteAlertBox
          open={deleteAlertOpen}
          onClose={() => setDeleteAlertOpen(false)}
          onDelete={handleDeleteConfirm}
          currDarkThemeClass={currDarkThemeClass}
          title={`Delete Driver: ${driverToDelete?.driver_name || ''}`}
          headContentText="This will delete the driver from both Metrc and the database. This action cannot be undone."
        />
      </Box>
    </Fade>
  );
}
